"use client"

import { useState, type FC } from "react"
import getStickyContentBlockLeftProps from "@/utils/getStickyContentBlockLeftProps"
import {
  AspectRatio,
  Box,
  Icon,
  Image,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"

import { type VideoBlock as VideoBlockType } from "@/types/database"
import BlurFade from "@/components/Animations/BlurFade"
import { PlayIcon } from "@/components/icons"

import ContentBlockTitle, {
  type ContentBlockTitleNumberProps,
} from "./ContentBlockTitle"

interface VideoBlockProps extends ContentBlockTitleNumberProps {
  block: VideoBlockType
}

const getVideoUrl = (videoUrl: string) => {
  const trimmedUrl = videoUrl.trim()

  if (/^(?:https?:)?\/\//.test(trimmedUrl)) {
    return trimmedUrl.startsWith("//") ? `https:${trimmedUrl}` : trimmedUrl
  }

  if (
    /^(?:www\.)?(?:youtube\.com|youtube-nocookie\.com|youtu\.be|vimeo\.com)\//i.test(
      trimmedUrl
    )
  ) {
    return `https://${trimmedUrl}`
  }

  return trimmedUrl
}

const getYouTubeVideoId = (videoUrl: string) => {
  try {
    const url = new URL(getVideoUrl(videoUrl))
    const hostname = url.hostname.replace(/^www\./, "")

    if (
      hostname === "youtube.com" ||
      hostname === "m.youtube.com" ||
      hostname === "youtube-nocookie.com"
    ) {
      if (url.pathname === "/watch") {
        return url.searchParams.get("v")
      }

      if (url.pathname.startsWith("/embed/")) {
        return url.pathname.split("/embed/")[1]?.split("/")[0] ?? null
      }

      if (url.pathname.startsWith("/live/")) {
        return url.pathname.split("/live/")[1]?.split("/")[0] ?? null
      }

      if (url.pathname.startsWith("/shorts/")) {
        return url.pathname.split("/shorts/")[1]?.split("/")[0] ?? null
      }

      if (url.pathname.startsWith("/v/")) {
        return url.pathname.split("/v/")[1]?.split("/")[0] ?? null
      }
    }

    if (hostname === "youtu.be") {
      return url.pathname.slice(1).split("/")[0] || null
    }
  } catch {
    return null
  }

  return null
}

const getVimeoVideoId = (videoUrl: string) => {
  try {
    const url = new URL(getVideoUrl(videoUrl))

    if (url.hostname.replace(/^www\./, "") === "vimeo.com") {
      return url.pathname.slice(1).split("/")[0] || null
    }
  } catch {
    return null
  }

  return null
}

const getYouTubeThumbnailUrl = (videoId: string) =>
  `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`

const VideoContent: FC<{ videoUrl: string; title: string }> = ({
  title,
  videoUrl,
}) => {
  const youTubeVideoId = getYouTubeVideoId(videoUrl)
  const normalizedVideoUrl = getVideoUrl(videoUrl)
  const [isYouTubePlaying, setIsYouTubePlaying] = useState(false)

  if (youTubeVideoId) {
    if (isYouTubePlaying) {
      return (
        <AspectRatio ratio={16 / 9} w="full">
          <iframe
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            src={`https://www.youtube-nocookie.com/embed/${youTubeVideoId}?rel=0&autoplay=1`}
            style={{ border: 0 }}
            title={title}
          />
        </AspectRatio>
      )
    }

    return (
      <Box
        _hover={{
          "& [data-video-play]": {
            bg: "primary.500",
            transform: "translate(-50%, -50%) scale(1.06)",
          },
          "& img": {
            transform: "scale(1.03)",
          },
        }}
        appearance="none"
        asChild
        bg="transparent"
        border="0"
        cursor="pointer"
        display="block"
        p={0}
        textAlign="inherit"
        w="full"
      >
        <button
          aria-label={`Play ${title}`}
          onClick={() => setIsYouTubePlaying(true)}
          type="button"
        >
          <AspectRatio
            bg="bg.muted"
            borderColor="border.muted"
            borderRadius="lg"
            borderWidth="1px"
            overflow="hidden"
            ratio={16 / 9}
            w="full"
          >
            <Box position="relative" w="full">
              <Image
                alt=""
                h="full"
                loading="lazy"
                objectFit="cover"
                src={getYouTubeThumbnailUrl(youTubeVideoId)}
                transition="transform 0.2s ease"
                w="full"
              />
              <Box
                bg="blackAlpha.700"
                borderRadius="full"
                color="white"
                data-video-play=""
                display="grid"
                h={14}
                left="50%"
                placeItems="center"
                position="absolute"
                top="50%"
                transform="translate(-50%, -50%)"
                transition="background-color 0.2s ease, transform 0.2s ease"
                w={14}
              >
                <Icon as={PlayIcon} boxSize={6} ml="0.125rem" />
              </Box>
            </Box>
          </AspectRatio>
        </button>
      </Box>
    )
  }

  const vimeoVideoId = getVimeoVideoId(videoUrl)

  if (vimeoVideoId) {
    return (
      <AspectRatio ratio={16 / 9}>
        <iframe
          allowFullScreen
          loading="lazy"
          src={`https://player.vimeo.com/video/${vimeoVideoId}`}
          style={{ border: 0 }}
          title={title}
        />
      </AspectRatio>
    )
  }

  return (
    <video autoPlay controls muted playsInline style={{ width: "100%" }}>
      <source src={normalizedVideoUrl} type="video/mp4" />
    </video>
  )
}

const VideoBlock: FC<VideoBlockProps> = ({ block, titleNumber }) => {
  const {
    darker_bg,
    headline,
    padding,
    title = "Video",
    two_columns_video,
    video,
    video_url,
  } = block
  const trimmedTitle = title?.trim()
  const trimmedHeadline = headline?.trim()
  const hasTitle =
    trimmedTitle !== undefined && trimmedTitle !== "" && trimmedTitle !== null
  const hasDescription = Boolean(trimmedHeadline)
  const hasHeader = hasTitle || hasDescription

  // video.id takes priority over video_url
  const resolvedVideoUrl = video?.id
    ? `${process.env.DIRECTUS_ASSETS_URL ?? ""}${video.id}`
    : (video_url ?? "")

  //TODO: add option to show video in full width
  return (
    <Stack
      alignItems="flex-start"
      bgColor={darker_bg ? "bg.subtle" : "transparent"}
      className="video-block  edge-to-edge"
      direction={{ base: "column", lg: two_columns_video ? "row" : "column" }}
      gap={{ base: 8, lg: two_columns_video ? 6 : 8 }}
      px={{
        base: padding ? 8 : 0,
        md: padding ? 10 : 0,
        lg: padding ? 24 : 0,
        xl: padding ? 40 : 0,
      }}
    >
      {hasHeader && (
        <VStack
          alignItems="flex-start"
          flexGrow={1}
          flexShrink={0}
          maxWidth={{
            base: "100%",
            lg: two_columns_video ? "30%" : "100%",
          }}
          minW={0}
          pt={{ base: 12, lg: 24 }}
          px={{ base: 8, md: 10, lg: 24, xl: 40 }}
          w={{
            base: "100%",
            lg: two_columns_video ? "30%" : "100%",
          }}
          {...getStickyContentBlockLeftProps(two_columns_video)}
        >
          <BlurFade flexDirection="column" gap={0} inView>
            {hasTitle && (
              <ContentBlockTitle
                fontWeight="bold"
                title={trimmedTitle}
                titleNumber={titleNumber}
              />
            )}
            {hasDescription && (
              <Text as="p" color="fg.muted" fontFamily="mono" fontSize="sm">
                {trimmedHeadline}
              </Text>
            )}
          </BlurFade>
        </VStack>
      )}
      <VStack
        alignItems="flex-start"
        flexGrow={1}
        maxWidth={{
          base: "100%",
          lg: two_columns_video && hasHeader ? "70%" : "100%",
        }}
        minW={0}
        pl={!hasHeader && two_columns_video ? { base: 0, lg: 8 } : 0}
        w={{
          base: "100%",
          lg: two_columns_video && hasHeader ? "70%" : "100%",
        }}
      >
        {resolvedVideoUrl ? (
          <VideoContent title={title} videoUrl={resolvedVideoUrl} />
        ) : null}
      </VStack>
    </Stack>
  )
}

export default VideoBlock
